import { $t } from '@/i18n';
import { message } from '@utils/wuji-message';

import { writeClipboard } from '@utils/clipboard';
import { eventEmitterAtomId } from '@components/pagelet-component/flowAtom/event-emitter';
import { isPageletComponent, isPageletComponentRenderer } from '@components/pagelet-component/utils';

import { toString } from '@/utils/toString';
import { XyLessCodeMethodSelectorMeta } from '../paramsFormComponents';
import { CATEGORY } from './const';
import pageletAtoms from './pageletAtoms';
import diff3Atoms from './diff3Atoms';

// 复制文本
export const copyTextAtom = {
  id: 'uicore:copyText',
  name: $t('k_2b78c99b'),
  category: CATEGORY.LOGIC,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'copyValue',
        type: 'string',
        title: $t('k_fd314ad8'),
        default: $t('k_f9f2ba58'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'isRemind',
        title: $t('k_fa3e9dc4'),
        type: 'boolean',
        default: true,
      },
    ],
  },
  execute: async (ctx, params) => {
    const { isRemind = true } = params;
    await writeClipboard(toString(params.copyValue));
    if (isRemind) {
      message.success($t('k_e7624279'));
    }
  },
};
// 调用 LessCode 方法
export const invokeLessCodeAtom = {
  id: 'xy:invokeLessCode',
  name: $t('k_ea9668c3'),
  category: CATEGORY.LOGIC,
  desc: $t('k_6c4a4a7'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'methodName',
        type: 'string',
        title: $t('k_136b2e90'),
        ui: { type: 'XyLessCodeMethodSelector' },
      },
      {
        id: 'abortWhenReturnFalse',
        type: 'boolean',
        default: true,
        title: $t('k_668dcab'),
      },
      {
        id: 'args',
        type: 'array',
        'uc:allowInterop': true,
        title: $t('k_bc85a2de'),
        description: $t('k_132f3e5c'),
        items: {
          type: 'string',
          ui: { type: 'exprInput', props: { type: 'interopable-string' } },
        },
      },
    ],
  },
  returnValue: $t('k_ac5168de'),
  paramsFormComponents: [
    XyLessCodeMethodSelectorMeta,
  ],
  async execute(ctx, params) {
    let { methodName } = params;
    const { abortWhenReturnFalse, args } = params;

    let context = null;
    if (methodName?.startsWith('$app.')) {
      methodName = methodName.replace('$app.', '');
      context = Vue.prototype.$app;
    } else {
      // 目前逍遥只能在最外层编写 LessCode，所以限制一下先
      let { renderer } = ctx;
      // 但是要注意在线组件的场景下，组件的renderer就是最外层的renderer
      while (renderer.ucParentRenderer && !isPageletComponentRenderer(renderer.$el)) {
        renderer = renderer.ucParentRenderer;
      }
      context = renderer;
    }

    if (typeof context[methodName] !== 'function') {
      throw new Error($t('k_2742a48a', { arg_0: methodName }));
    }

    const ans = await context[methodName](...args || []);
    if (ans === false && abortWhenReturnFalse) ctx.abort();
    return ans;
  },
};

export const debuggerAtom = {
  id: 'xy:debugger',
  name: 'debugger ',
  desc: $t('k_6df3c2fa'),
  category: CATEGORY.LOGIC,
  async execute(ctx) {
    // eslint-disable-next-line no-new-func
    new Function('ctx', 'console.log(\'Flow Ctx\', ctx); debugger')(ctx);
  },
};


// 仅在在线组件的编辑器中可见
const PageletComponentEventEmitterAtom = {
  id: eventEmitterAtomId,
  name: $t('k_461604b1'),
  desc: $t('k_16369f69'),
  category: CATEGORY.LOGIC,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'eventKey',
        type: 'string',
        title: $t('k_b641daa8'),
      }, {
        id: 'eventName',
        type: 'string',
        title: $t('k_6c5950ca'),
      },
      {
        id: 'eventPayload',
        type: 'string',
        title: $t('k_d4a9d62c'),
        ui: {
          type: 'exprInput',
          props: {
            type: 'javascript',
            multipleLine: true,
            placeholder:
              $t('k_179d8e4a'),
          },
        },
      },
      {
        id: 'eventDesc',
        type: 'string',
        title: $t('k_b78a92ae'),
      },
    ],
  },
  async execute(ctx, params) {
    const payload = ctx.tryEval(params.eventPayload);
    let currentVnode = ctx.renderer;
    // 找到最近的在线组件容器
    while (!isPageletComponent(currentVnode.$el) && currentVnode.$parent) {
      currentVnode = currentVnode.$parent;
    }
    currentVnode.$emit(params.eventKey, payload);
  },
  returnValue: null,
  visible: false,
};


export default [
  invokeLessCodeAtom,
  ...pageletAtoms,
  copyTextAtom,
  ...diff3Atoms,
  debuggerAtom,
  PageletComponentEventEmitterAtom,
];
